/**
 * Utility functions for filtering archived users in the frontend
 */

/**
 * Filter out archived users from an array
 * @param {Array} users - Array of user objects
 * @returns {Array} - Filtered array without archived users
 */
export const filterActiveUsers = (users) => {
  if (!Array.isArray(users)) return users;
  return users.filter(user => !user.archived);
};

/**
 * Check if a user is archived
 * @param {Object} user - User object
 * @returns {boolean} - True if user is archived
 */
export const isUserArchived = (user) => user?.archived === true;

/**
 * Filter archived users from team members
 * @param {Array} teams - Array of team objects with users
 * @returns {Array} - Teams with filtered users
 */
export const filterTeamMembers = (teams) => {
  if (!Array.isArray(teams)) return teams;

  return teams.map(team => ({
    ...team,
    users: filterActiveUsers(team.users || []),
    usersOnTeams: team.usersOnTeams ? team.usersOnTeams.filter(userOnTeam => !isUserArchived(userOnTeam.user)) : [],
  }));
};

/**
 * Get archived status badge info
 * @param {Object} user - User object
 * @returns {Object|null} - Badge configuration or null if not archived
 */
export const getArchivedBadge = (user) => {
  if (!isUserArchived(user)) return null;

  return {
    text: 'Archived',
    variant: 'secondary',
    className: 'ms-2',
  };
};

/**
 * Create user display name with archived indicator
 * @param {Object} user - User object
 * @returns {string} - Display name with archived indicator if needed
 */
export const getUserDisplayName = (user) => {
  if (!user) return '';

  const name = `${user.firstName || ''} ${user.lastName || ''}`.trim();
  return isUserArchived(user) ? `${name} (Archived)` : name;
};

/**
 * Filter user options for dropdowns/selects
 * @param {Array} users - Array of user objects
 * @param {boolean} includeArchived - Whether to include archived users
 * @returns {Array} - Array of {label, value} options
 */
export const createUserOptions = (users, includeArchived = false) => {
  const filteredUsers = includeArchived ? users : filterActiveUsers(users);

  return filteredUsers.map(user => ({
    label: getUserDisplayName(user),
    value: user.id,
    disabled: isUserArchived(user),
  }));
};
